import { ManageYourPlanDialog } from 'app/components/subscriptions';
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';
import { Context, ManageYourPlanDialogPayload } from './types';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';

const ManageYourPlanDialogContext = createContext<Context>({} as Context);

export function ManageYourPlanDialogProvider({ children }: PropsWithChildren) {
  const isSubscriptionsFeatureEnabled = useFlag(FeatureName.SUBSCRIPTIONS);

  const [manualPayload, setManualPayload] =
    useState<ManageYourPlanDialogPayload | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const match = useMatch('/equipment/:id');

  const equipmentIdFromUrl = match?.params.id;
  const isModalOpenInUrl = searchParams.get('modal') === 'manage-plan';

  const isUrlFlowActive = !!equipmentIdFromUrl && isModalOpenInUrl;

  const {
    equipmentListData: { selectedEquipment: equipmentFromUrl, isInitialized },
  } = useContext(EquipmentListContext);

  const openDialog = (payload: ManageYourPlanDialogPayload) => {
    if (!payload.equipment.telematics) {
      console.error('Equipment must be telematics');
      handleClose();
    }

    if (match && match.params.id === payload.equipment.id) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('modal', 'manage-plan');
      setSearchParams(newParams);
    } else {
      setManualPayload(payload);
    }
  };

  function handleClose() {
    if (isUrlFlowActive) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('modal');
      setSearchParams(newParams);
    } else {
      setManualPayload(null);
    }
  }

  useEffect(() => {
    if (isUrlFlowActive && isInitialized && equipmentFromUrl) {
      if (!equipmentFromUrl.telematics) {
        console.error('Equipment must be telematics');
        handleClose();
      }
    }
  }, [isUrlFlowActive, isInitialized, equipmentFromUrl]);

  let equipmentToShow: IEquipmentEntry | null = null;
  let onBackCallback: (() => void) | null = null;

  if (isUrlFlowActive) {
    const isDataReady = !!isInitialized && !!equipmentFromUrl?.telematics;

    if (isDataReady) {
      equipmentToShow = equipmentFromUrl!;
      onBackCallback = () => {
        handleClose();
      };
    }
  } else if (manualPayload) {
    equipmentToShow = manualPayload.equipment;
    onBackCallback = () => {
      manualPayload.onBack?.();
      handleClose();
    };
  }

  return (
    <ManageYourPlanDialogContext.Provider
      value={{
        openDialog,
      }}
    >
      {children}

      {equipmentToShow && onBackCallback && isSubscriptionsFeatureEnabled && (
        <ManageYourPlanDialog
          equipment={equipmentToShow}
          onBack={onBackCallback}
        />
      )}
    </ManageYourPlanDialogContext.Provider>
  );
}

const useManageYourPlanDialog = () => useContext(ManageYourPlanDialogContext);
export default useManageYourPlanDialog;
